import { FunctionComponent } from 'react';
import { YourDetailsTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import { useUserData } from '@hooks';
import { yourDetailsSchema } from '../../validations';
import {
  msalInstance,
  handleLoginPageEvent,
  parseZodErrors,
  LoginRequestConfig,
  handleLoginRequest,
} from '@utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { z, ZodError } from 'zod';
import {
  useBuskingStore,
  useBuskingPayload,
  useHandleSubmit,
} from '../../../../hooks';
import { phoneNumberValidation } from '@shared/schemas';

export const YourDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, get, update } = useBuskingStore();
  const contactUserOption = get('contactUserOption').toLowerCase() === 'yes';

  const formData = {
    firstName: get('firstName'),
    lastName: get('lastName'),
    email: get('email'),
    ...(contactUserOption ? { phoneNumber: get('phoneNumber') } : {}),
  };
  const { mutate: submitForm, isPending } = useHandleSubmit({
    confirmationRoute: RoutesList.CONFIRMATION,
    endpoint: '/report/v1/busking-and-street-entertainment',
  });
  const payload = useBuskingPayload();
  const [crmId, setCrmId] = useState<string>('');
  const [idProvider, setIdProvider] = useState<string>('');
  const { isLoggedInUser, contactData, isLoading, isFetching } =
    useUserData(crmId);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const isLoadingUserData = isLoading || isFetching;

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }

    if (contactData) {
      const UserData = {
        email: contactData.emailaddress1 || '',
        firstName: contactData.firstname || '',
        lastName: contactData.lastname || '',
        phoneNumber: contactData?.mobilephone || '',
      };
      update('firstName', UserData.firstName);
      update('lastName', UserData.lastName);
      update('email', UserData.email);
      update('phoneNumber', UserData.phoneNumber as string);
    }
  }, [contactData, crmId, idProvider]);

  const handleSubmit = () => {
    const { firstName, lastName, email, phoneNumber } = formData;
    const dynamicYourDetailSchema = contactUserOption
      ? yourDetailsSchema.extend({
          phoneNumber: phoneNumberValidation(
            'Please enter your phone number',
            'Enter a valid phone number',
          ),
        })
      : yourDetailsSchema.extend({
          phoneNumber: z.string().optional(),
        });
    try {
      dynamicYourDetailSchema.parse({
        firstName,
        lastName,
        email,
        phoneNumber,
      });

      submitForm({
        payload,
        isLoggedInUser,
      });
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrorMessages = parseZodErrors(error);
        setErrorMessages(newErrorMessages);
        throw new Error();
      }
    }
  };

  const loginConfig: LoginRequestConfig = {
    msal: msalInstance,
    navigate: () => navigate(RoutesList.YOUR_DETAILS, { replace: true }),
  };

  const handleLogin = () => handleLoginRequest(loginConfig);

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.CONTACT_USER_OPTION);
    },
  };

  const buttonNext = {
    label: 'Submit',
    loading: isPending,
    onClick: () => {
      handleSubmit();
    },
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    if (name === 'firstName') {
      update('firstName', value);
    } else if (name === 'lastName') {
      update('lastName', value);
    } else if (name === 'email') {
      update('email', value);
    } else if (name === 'phoneNumber') {
      update('phoneNumber', value);
    }
    setErrorMessages([]);
  };

  return (
    <YourDetailsTemplate
      isLoadingUserData={isLoadingUserData}
      isLoggedInUser={isLoggedInUser}
      userData={formData}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorMessages={errorMessages}
      handleLogin={handleLogin}
      onChange={handleChange}
      idProvider={idProvider}
    />
  );
};
